import type { CopyPricePreviewItem } from '@/api';
import {
  formatLaunchStatusDisplay,
  formatPriceField,
  formatRm,
} from '@/components/table/table-format-helpers';
import {
  applyNumericFilter,
  compareNumericValues,
  getFontColor,
} from '@/components/table/table-helpers';
import type { NumericFilterValue } from '@/components/table/table-types';
import FilterDropdown from '@/components/table/toolbar/filter-dropdown';
import NumericColumnFilter from '@/components/table/toolbar/numeric-column-filter';
import { connectColors, themeColors } from '@/theme/colors';
import { Button } from '@lego/connect-components-react';
import { Flex, Text } from '@lego/klik-ui';
import {
  Tooltip as MuiTooltip,
  ThemeProvider,
  createTheme,
} from '@mui/material';
import {
  type MRT_Cell,
  type MRT_ColumnDef,
  MRT_GlobalFilterTextField,
  MRT_ShowHideColumnsButton,
  MRT_ToggleDensePaddingButton,
  MRT_ToggleFiltersButton,
  MRT_ToggleFullScreenButton,
  MaterialReactTable,
} from 'material-react-table';
import {
  type ColumnCategory,
  type ColumnGroup,
  type CopyPricePreviewItemForDisplay,
  type CopyPricePreviewTableProps,
  columnData,
  columnGroups,
  numericFilterColumns,
  percentageColumns,
} from './copy-price-preview-table-config';

const CopyPricePreviewTable = ({
  items,
  isExecuting,
  wouldCopyCount,
  triggerRef,
  onConfirmCopy,
}: CopyPricePreviewTableProps) => {
  const formattedItems = items.map((item) => ({
    ...item,
    launchStatus: formatLaunchStatusDisplay(item.launchStatus),
    pricingStatus: item.pricingStatus?.slice(0, 1) ?? '',
    isSource: item.isSource ? 'from' : 'to',
    rrp: formatPriceField(item.rrp),
    retailMargin: formatRm(item.retailMargin),
    lp: formatPriceField(item.lp),
  }));

  const theme = createTheme({
    palette: {
      mode: 'light',
    },
  });

  const getHeaderCategoryBackgroundColor = (category?: ColumnCategory) => {
    switch (category) {
      case 'priceList':
        return connectColors.core.yellow[50];
      case 'productData':
        return connectColors.core.azure[200];
      case 'copy':
        return connectColors.core.slate[200];
      case 'copyData':
        return connectColors.core.blue[200];
      default:
        return connectColors.core.blue[200];
    }
  };

  const getHeaderCategorySubBackgroundColor = (category?: ColumnCategory) => {
    switch (category) {
      case 'priceList':
        return '#FFFCDA'; //color not present in Connect
      case 'productData':
        return connectColors.core.azure[50];
      case 'copy':
        return connectColors.core.slate[50];
      case 'copyData':
        return connectColors.core.blue[50];
      default:
        return connectColors.core.blue[50];
    }
  };

  const getFilterOptions = (
    key: keyof CopyPricePreviewItemForDisplay,
    data: CopyPricePreviewItemForDisplay[],
  ): { id: string; label: string }[] => {
    return Array.from(new Set(data.map((row) => String(row[key] ?? ''))))
      .sort(compareNumericValues)
      .map((value) => ({
        id: value,
        label: value === '' ? '(empty)' : value,
      }));
  };
  const getCellComponent = (
    cell: MRT_Cell<CopyPricePreviewItemForDisplay>,
    key: keyof CopyPricePreviewItemForDisplay,
  ) => {
    return (
      <Text className="body-xs-regular" isTruncated>
        {cell.row.original[key]}
      </Text>
    );
  };

  const getColumns = (): MRT_ColumnDef<CopyPricePreviewItemForDisplay>[] => {
    const getHeaderGroup = (
      key: keyof CopyPricePreviewItem,
    ): ColumnGroup | undefined =>
      columnGroups?.find((group) => group.columnKeys.includes(key));

    const dataColumns = (
      key: keyof CopyPricePreviewItem,
    ): MRT_ColumnDef<CopyPricePreviewItemForDisplay> => ({
      accessorKey: key,
      header: columnData[key]?.label ?? key,
      size: columnData[key]?.size,
      aggregationFn: columnData[key]?.aggregationFn,
      ...(numericFilterColumns.has(key) && {
        sortingFn: (rowA, rowB, columnId) =>
          compareNumericValues(
            rowA.getValue(columnId),
            rowB.getValue(columnId),
          ),
      }),
      enablePinning: false,
      muiTableBodyCellProps: {
        align: columnData[key]?.alignment ?? 'left',
      },
      muiTableHeadCellProps: {
        align: columnData[key]?.alignment ?? 'left',
        sx: {
          backgroundColor: getHeaderCategorySubBackgroundColor(
            getHeaderGroup(key)?.columnCategory,
          ),

          //the only way to color pinned headers :/
          '&[data-pinned="true"]::before': {
            backgroundColor: getHeaderCategorySubBackgroundColor(
              getHeaderGroup(key)?.columnCategory,
            ),
          },
        },
      },
      enableColumnFilter: true,
      filterFn: (row, _columnId, filterValue) => {
        const value = row.original[key];
        if (numericFilterColumns.has(key)) {
          return applyNumericFilter(
            value ?? '',
            filterValue as NumericFilterValue,
          );
        }

        return filterValue.includes(String(value ?? ''));
      },
      Header: () => (
        <MuiTooltip title={columnData[key]?.label} placement="top">
          <Flex alignItems="center" gap="4px" minHeight={'28px'}>
            <Text
              className="body-xs-medium"
              color={getFontColor(columnData[key]?.columnType)}
            >
              {columnData[key]?.label}
            </Text>
          </Flex>
        </MuiTooltip>
      ),
      Cell: ({ cell }) => {
        return getCellComponent(cell, key);
      },
      Filter: ({ header }) => {
        if (numericFilterColumns.has(key)) {
          return (
            <NumericColumnFilter
              valueOptions={getFilterOptions(
                key,
                header.column
                  .getFacetedRowModel()
                  .flatRows.map((row) => row.original),
              )}
              value={
                header.column.getFilterValue() as NumericFilterValue | undefined
              }
              onChange={(value) => header.column.setFilterValue(value)}
              isPercentage={percentageColumns.has(key)}
            />
          );
        }
        return (
          <FilterDropdown
            options={getFilterOptions(
              key,
              header.column
                .getFacetedRowModel()
                .flatRows.map((row) => row.original),
            )}
            onChange={(selected) =>
              header.column.setFilterValue(
                selected.length > 0 ? selected : undefined,
              )
            }
            value={header.column.getFilterValue() as string[]}
          />
        );
      },
    });
    if (columnGroups) {
      return columnGroups.map((group) => {
        return {
          id: group.id,
          header: group.header,
          enableResizing: false,
          muiTableHeadCellProps: {
            sx: {
              backgroundColor: getHeaderCategoryBackgroundColor(
                group.columnCategory,
              ),
              fontWeight: 'bold',
              height: '28px',
              //unfortunately there's no simpler way to align the group text to the left
              '& .Mui-TableHeadCell-Content': {
                justifyContent: 'left',
                paddingLeft: '8px',
              },
            },
          },
          columns: group.columnKeys.map(dataColumns),
        };
      });
    }
    return Object.keys(columnData).map((key) =>
      dataColumns(key as keyof CopyPricePreviewItem),
    );
  };

  const columns = getColumns();

  return (
    <ThemeProvider theme={theme}>
      <MaterialReactTable
        columns={columns}
        data={formattedItems}
        enableStickyHeader
        enablePagination={false}
        enableColumnActions={false}
        enableSorting
        enableRowVirtualization
        enableColumnResizing
        enableFacetedValues
        layoutMode="grid"
        initialState={{
          density: 'compact',
          showColumnFilters: true,
          showGlobalFilter: true,
        }}
        muiFilterTextFieldProps={{ placeholder: 'Filter' }}
        muiTableContainerProps={({ table }) => ({
          sx: {
            height: table.getState().isFullScreen
              ? 'calc(100vh - 100px)'
              : 'calc(100vh - 300px)',
            overflow: 'auto',
          },
        })}
        renderTopToolbar={({ table }) => (
          <Flex margin="8px 16px 8px" gap="8px" alignItems="center">
            <MRT_GlobalFilterTextField table={table} placeholder="Search" />
            <MRT_ToggleFiltersButton table={table} />
            <MRT_ShowHideColumnsButton table={table} />
            <MRT_ToggleDensePaddingButton table={table} />
            <MRT_ToggleFullScreenButton table={table} />
            {table.getState().columnFilters.length > 0 && (
              <Flex gap="4px" alignItems="center">
                <Text
                  className="label-sm-medium"
                  color={themeColors.content.subtle}
                  whiteSpace="nowrap"
                >
                  {table.getState().columnFilters.length}{' '}
                  {table.getState().columnFilters.length === 1
                    ? 'filter'
                    : 'filters'}{' '}
                  applied
                </Text>
                <Button
                  appearance="ghost"
                  size="small"
                  onClick={() => table.resetColumnFilters()}
                  iconName="control-close"
                >
                  Clear all filters
                </Button>
              </Flex>
            )}
            <Flex ml="auto" paddingY="4px" paddingX="4">
              <span ref={triggerRef}>
                <Button
                  size="small"
                  variant="primary"
                  appearance="solid"
                  disabled={isExecuting || wouldCopyCount === 0}
                  onClick={onConfirmCopy}
                >
                  {isExecuting ? 'Copying…' : 'Publish prices'}
                </Button>
              </span>
            </Flex>
          </Flex>
        )}
        renderBottomToolbar={({ table }) => (
          <Flex my={1} mx={2}>
            <Text fontSize="11px">
              {table.getFilteredRowModel().rows.length.toLocaleString()} rows
            </Text>
          </Flex>
        )}
      />
    </ThemeProvider>
  );
};

export default CopyPricePreviewTable;
